import { useEffect, useCallback, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { usageTracker } from '../utils/usageTracker';
import { DEFAULT_LIMIT } from '../constants/filters';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { usePrefetchTicketData } from '../hooks/usePrefetchTicketData';
import { TicketListingsTable } from '../components/TicketListingsTable';
import { Flex } from 'foundations-components/components/Flex';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { TicketListingsGrid } from '../components/TicketListingsGrid';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { TicketListingView } from '../constants/filters';
import { TicketListingsHeader } from '../components/TicketListingsHeader';
import { ActiveFilterPills } from '../components/ActiveFilterPills';
import { useIsPreview } from '../hooks/useIsPreview';
import { FAKE_THREAD_ID, augmentTicketWithPlaceholders } from '../data/previewMocks';
import { toSearchTicket } from '../data/searchTickets';
import { usePortalData } from '../data/usePortalData';
import { useIsPillBorderRadius, useBreakpoint } from 'portals-platform-lib';
import { PAGE_CONTENT_SPACING } from '../constants/layout';
import { useResolvedTicketListings } from '../data/useResolvedTicketListings';
import { usePortalPageTitle } from '../hooks/usePortalPageTitle';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function TicketListings() {
  const isPreview = useIsPreview();
  const {
    data: portalData
  } = usePortalData();
  useDocumentTitle(usePortalPageTitle());
  const ticketListingsSortAndFilters = useTicketListingsSortAndFilters();
  const isMobile = useBreakpoint() === 'mobile';
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const {
    view,
    offset,
    setOffset,
    searchString,
    sort,
    setSort,
    ticketView,
    statusFilter,
    createDateFilter,
    lastActivityFilter,
    hasActiveFilters
  } = ticketListingsSortAndFilters;
  const prefetchTicketData = usePrefetchTicketData();
  const {
    toTicketDetails,
    consumeLastOpenedTicketId
  } = useNavigationContext();
  const ticketListings = useResolvedTicketListings({
    sort,
    offset,
    ticketView,
    statusFilter,
    searchString,
    createDateFilter,
    lastActivityFilter
  });
  const {
    total: listingTotal,
    loading: isLoading,
    responseTotal
  } = ticketListings;
  useEffect(() => {
    usageTracker.track('Customer Portal App', 'Page Viewed', {
      screen: 'ticket-listings'
    });
  }, []);
  useEffect(() => {
    if (!isPreview && !isLoading && responseTotal !== undefined && (offset < 0 || offset >= responseTotal) && offset !== 0) {
      setOffset(0);
    }
  }, [isPreview, isLoading, offset, responseTotal, setOffset]);
  const onRowClick = useCallback(ticketId => {
    if (isLoading) return;
    if (isPreview) {
      toTicketDetails(ticketId, FAKE_THREAD_ID);
      return;
    }
    usageTracker.track('Customer Portal App', 'Tickets Discovered', {
      action: 'ticket-expanded',
      screen: 'ticket-listings',
      initiator: view === TicketListingView.GRID || isMobile ? 'grid-card-click' : 'table-row-click'
    });
    toTicketDetails(ticketId);
  }, [isLoading, isPreview, view, isMobile, toTicketDetails]);
  const onRowHover = useCallback(ticketId => {
    prefetchTicketData(ticketId);
  }, [prefetchTicketData]);
  const data = useMemo(() => {
    var _ticketListings$data2;
    if (isLoading) {
      return new Array(DEFAULT_LIMIT).fill(null).map((_, index) => toSearchTicket({
        hs_ticket_id: index + 1,
        status: 'OPEN'
      }));
    }
    if (isPreview) {
      var _portalData$configure, _ticketListings$data;
      const configuredListProperties = (_portalData$configure = portalData === null || portalData === void 0 ? void 0 : portalData.configuredTicketListProperties) !== null && _portalData$configure !== void 0 ? _portalData$configure : [];
      return ((_ticketListings$data = ticketListings.data) !== null && _ticketListings$data !== void 0 ? _ticketListings$data : []).map(ticket => toSearchTicket(augmentTicketWithPlaceholders(ticket, configuredListProperties)));
    }
    return (_ticketListings$data2 = ticketListings.data) !== null && _ticketListings$data2 !== void 0 ? _ticketListings$data2 : [];
  }, [isLoading, isPreview, portalData === null || portalData === void 0 ? void 0 : portalData.configuredTicketListProperties, ticketListings.data]);
  useEffect(() => {
    var _ref, _element$querySelecto;
    if (isLoading || data.length === 0) return;
    const ticketId = consumeLastOpenedTicketId();
    if (ticketId === undefined) return;
    const element = document.getElementById(String(ticketId));
    (_ref = (_element$querySelecto = element === null || element === void 0 ? void 0 : element.querySelector('[data-focus-restore-target]')) !== null && _element$querySelecto !== void 0 ? _element$querySelecto : element) === null || _ref === void 0 || _ref.focus();
  }, [isLoading, data, consumeLastOpenedTicketId]);
  const hasCreatedateColumn = useHasConfiguredColumn('createdate');
  const showSortingHelper = useMemo(() => !hasCreatedateColumn && sort.some(f => f.propertyName === 'createdate'), [hasCreatedateColumn, sort]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "INDEX_LOAD",
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      "aria-live": "polite",
      "aria-atomic": "true",
      children: !isLoading && unescapedText('customer-portal-app.records', {
        count: listingTotal
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      paddingInline: isMobile ? '400' : PAGE_CONTENT_SPACING,
      paddingBlockStart: "200",
      children: [/*#__PURE__*/_jsx(TicketListingsHeader, {
        totalCount: listingTotal
      }), /*#__PURE__*/_jsx(ActiveFilterPills, {}), /*#__PURE__*/_jsxs("div", {
        children: [view === TicketListingView.GRID || isMobile ? /*#__PURE__*/_jsx(TicketListingsGrid, {
          data: data,
          isLoading: isLoading,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          isUsingPillBorderRadius: isUsingPillBorderRadius,
          error: !!ticketListings.error,
          hasActiveFilters: hasActiveFilters
        }) : /*#__PURE__*/_jsx(TicketListingsTable, {
          data: data,
          isLoading: isLoading,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          sort: sort,
          setSort: setSort,
          isUsingPillBorderRadius: isUsingPillBorderRadius,
          error: !!ticketListings.error,
          hasActiveFilters: hasActiveFilters
        }), showSortingHelper && /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          textAlign: "end",
          typography: "detail-200",
          color: "text-core-default",
          "data-test-id": "sorting-helper",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.sortingHelper"
          })
        })]
      }), listingTotal > DEFAULT_LIMIT && /*#__PURE__*/_jsx(Paginator, {
        "data-test-id": "listings-paginator",
        marginBlockStart: "600",
        pageCount: Math.ceil(listingTotal / DEFAULT_LIMIT),
        page: Math.floor(offset / DEFAULT_LIMIT) + 1,
        maxVisiblePageButtons: 7,
        onPageChange: e => setOffset((e.target.value - 1) * DEFAULT_LIMIT)
      })]
    })]
  });
}